import { translate, getLanguageTagBCP47, magicConfig } from '@gig/magic-common';
import { USER_LOGOUT_KEYCLOAK, USER_LOGIN_KEYCLOAK, USER_RESET_PASSWORD_KEYCLOAK_QUERY, UPDATE_USER_STATUS, UPDATE_DEPOSIT_RESTRICTION, USER_PROFILE_DATA } from '@gig/magic-messages';
import { __awaiter, __generator, __assign } from 'tslib';
import { magic } from '@gig/magic-sdk';
import { notificationService, NotificationStyle } from '@gig/magic-messaging';
import { getKeycloakLocale } from '../../utils/keycloak.js';
import Keycloak from 'keycloak-js/lib/keycloak';
import { mapToUser, fetchProfileData } from './keycloak.profile.js';

var KeycloakService = /** @class */ (function () {
    function KeycloakService() {
        this.keycloak = null;
        this.refreshError = false;
        this.expireTimeout = null;
        this.buildUrlWithParams = function (baseUrl, params) {
            if (!params)
                return baseUrl;
            var queryParams = Object.entries(params)
                .filter(function (_a) {
                var _ = _a[0], value = _a[1];
                return value !== undefined && value !== null;
            })
                .map(function (_a) {
                var key = _a[0], value = _a[1];
                return encodeURIComponent(key) + "=" + encodeURIComponent(value);
            })
                .join('&');
            return queryParams ? baseUrl + "&" + queryParams : baseUrl;
        };
    }
    KeycloakService.prototype.init = function (config) {
        return __awaiter(this, void 0, void 0, function () {
            var error_1;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.keycloak = new Keycloak(this.getKeycloakConfig(config));
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 6, , 7]);
                        return [4 /*yield*/, this.keycloak.init(this.getKeycloakInit(config))];
                    case 2:
                        _a.sent();
                        if (!this.keycloak.authenticated) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.initUserSessionFromKeyCloak()];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        magic.session.destroy();
                        _a.label = 5;
                    case 5:
                        magic.events.sessionDestroyedCoreX(function () {
                            var redirectUri = window.location.origin;
                            // at this point the session is already destroyed by the sdk
                            // eg the local storage is cleared, etc
                            // we only have to reset the keycloak adapter state
                            var logoutUrl = _this.keycloak.createLogoutUrl({
                                redirectUri: redirectUri,
                            });
                            _this.keycloak.clearToken();
                            window.location.replace(logoutUrl);
                        });
                        return [3 /*break*/, 7];
                    case 6:
                        error_1 = _a.sent();
                        console.error('KeyCloak: Failed to initialize adapter:', error_1);
                        if (this.expireTimeout) {
                            clearTimeout(this.expireTimeout);
                        }
                        if (this.refreshError) {
                            this.keycloak.clearToken();
                            magic.session.destroy();
                            this.refreshError = false;
                        }
                        return [3 /*break*/, 7];
                    case 7:
                        window.commandBus.subscribe(USER_LOGOUT_KEYCLOAK, function (event) {
                            _this.logoutWithDelay(event.payload);
                        });
                        window.commandBus.subscribe(USER_LOGIN_KEYCLOAK, function (event) {
                            var _a, _b;
                            return _this.login((_a = event.payload) === null || _a === void 0 ? void 0 : _a.url, (_b = event.payload) === null || _b === void 0 ? void 0 : _b.params);
                        });
                        window.queryBus.subscribe(USER_RESET_PASSWORD_KEYCLOAK_QUERY, function () {
                            return _this.getResetPasswordUrl();
                        });
                        window.commandBus.subscribe(UPDATE_USER_STATUS, function () { return __awaiter(_this, void 0, void 0, function () {
                            var user, error_2;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        _a.trys.push([0, 2, , 3]);
                                        return [4 /*yield*/, this.getUserFromProfile()];
                                    case 1:
                                        user = _a.sent();
                                        magic.session.update(user);
                                        return [3 /*break*/, 3];
                                    case 2:
                                        error_2 = _a.sent();
                                        console.error('Failed to update user status:', error_2);
                                        return [3 /*break*/, 3];
                                    case 3: return [2 /*return*/];
                                }
                            });
                        }); });
                        this.userProfileUpdatedSubscription = window.eventBus.subscribe(UPDATE_DEPOSIT_RESTRICTION, function (event) { return __awaiter(_this, void 0, void 0, function () {
                            var user;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        if (!event.payload) return [3 /*break*/, 2];
                                        return [4 /*yield*/, this.getUserFromProfile()];
                                    case 1:
                                        user = _a.sent();
                                        magic.session.update(user);
                                        _a.label = 2;
                                    case 2: return [2 /*return*/];
                                }
                            });
                        }); });
                        this.userPromotionsSubscription = window.eventBus.subscribe(USER_PROFILE_DATA, function (event) { return __awaiter(_this, void 0, void 0, function () {
                            var user;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        if (!event.payload) return [3 /*break*/, 2];
                                        return [4 /*yield*/, this.getUserFromProfile()];
                                    case 1:
                                        user = _a.sent();
                                        magic.session.update(user);
                                        _a.label = 2;
                                    case 2: return [2 /*return*/];
                                }
                            });
                        }); });
                        window.queryBus.subscribe(USER_PROFILE_DATA, function () {
                            return _this.profileData;
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    KeycloakService.prototype.login = function (redirectUri, customParams) {
        return __awaiter(this, void 0, void 0, function () {
            var baseUrl;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.keycloak.createLoginUrl({
                            redirectUri: redirectUri,
                            locale: this.getLocale(),
                        })];
                    case 1:
                        baseUrl = _a.sent();
                        window.location.href = this.buildUrlWithParams(baseUrl, customParams);
                        return [2 /*return*/];
                }
            });
        });
    };
    KeycloakService.prototype.logout = function (redirectUri) {
        var logoutUrl = this.keycloak.createLogoutUrl({ redirectUri: redirectUri });
        this.keycloak.clearToken();
        magic.session.destroy();
        window.location.replace(logoutUrl);
    };
    KeycloakService.prototype.getResetPasswordUrl = function () {
        return this.keycloak.createLoginUrl({
            redirectUri: window.location.origin,
            action: 'UPDATE_PASSWORD',
            locale: this.getLocale(),
        });
    };
    KeycloakService.prototype.isAuthenticated = function () {
        return this.keycloak.authenticated;
    };
    KeycloakService.prototype.getKeycloakConfig = function (config) {
        var keycloak = config.keycloak;
        return {
            realm: keycloak.realm,
            url: keycloak.url,
            clientId: window.inTestEnvironment
                ? keycloak.apiClientId
                : keycloak.clientId,
        };
    };
    KeycloakService.prototype.getKeycloakInit = function (config) {
        var _a, _b;
        var keycloakSession = magic.session.getKeycloakSession();
        var locale = getKeycloakLocale();
        return __assign(__assign({ timeSkew: 0, scope: (_a = config.keycloak) === null || _a === void 0 ? void 0 : _a.realm, pkceMethod: 'S256', responseMode: 'query', checkLoginIframe: !window.inTestEnvironment, locale: locale }, keycloakSession), (((_b = config.keycloak) === null || _b === void 0 ? void 0 : _b.initOptions) || {}));
    };
    KeycloakService.prototype.initUserSessionFromKeyCloak = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var token, refreshToken, idToken, expiresAt, user;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!this.keycloak) {
                            return [2 /*return*/];
                        }
                        this.watchRefreshTokenError();
                        if (!this.tokenNeedsRefresh()) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.refreshAccessToken()];
                    case 1:
                        _b.sent();
                        _b.label = 2;
                    case 2:
                        token = this.keycloak.token;
                        refreshToken = this.keycloak.refreshToken;
                        idToken = this.keycloak.idToken;
                        expiresAt = (_a = this.keycloak.tokenParsed) === null || _a === void 0 ? void 0 : _a.exp;
                        magic.session.setKeycloakSession({
                            token: token,
                            refreshToken: refreshToken,
                            idToken: idToken,
                            expiresAt: expiresAt,
                        });
                        this.watchTokenExpired();
                        return [4 /*yield*/, this.getUserFromProfile()];
                    case 3:
                        user = _b.sent();
                        magic.session.create(user);
                        return [2 /*return*/];
                }
            });
        });
    };
    KeycloakService.prototype.getUserFromProfile = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, profile, magicProfile, documents;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, fetchProfileData()];
                    case 1:
                        _a = _b.sent(), profile = _a.profile, magicProfile = _a.magicProfile, documents = _a.documents;
                        this.profileData = profile;
                        return [2 /*return*/, mapToUser(this.keycloak.idTokenParsed.user_profile_id, this.keycloak.idTokenParsed.email, this.keycloak.sessionId, profile, magicProfile, documents)];
                }
            });
        });
    };
    KeycloakService.prototype.tokenNeedsRefresh = function () {
        return this.keycloak.isTokenExpired();
    };
    KeycloakService.prototype.watchTokenExpired = function () {
        var _this = this;
        if (this.expireTimeout) {
            return;
        }
        this.expireTimeout = setTimeout(function () { return __awaiter(_this, void 0, void 0, function () {
            var error_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.expireTimeout = null;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.refreshAccessToken()];
                    case 2:
                        _a.sent();
                        this.watchTokenExpired();
                        return [3 /*break*/, 4];
                    case 3:
                        error_3 = _a.sent();
                        console.error('KeyCloak: Failed to refresh token:', error_3);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); }, this.getExpireDelay());
    };
    KeycloakService.prototype.getExpireDelay = function () {
        var expireDateInMs = this.keycloak.tokenParsed.exp * 1000;
        var nowInMs = new Date().getTime();
        // attempt to refresh the token 10 seconds before it expires
        // if it expires in less than 10 seconds, refresh it immediately (zero delay)
        return Math.max(expireDateInMs - nowInMs - 10000, 0);
    };
    KeycloakService.prototype.watchRefreshTokenError = function () {
        var _this = this;
        this.keycloak.onAuthRefreshError = function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.refreshError = true;
                return [2 /*return*/];
            });
        }); };
    };
    KeycloakService.prototype.refreshAccessToken = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var token, refreshToken, idToken, expiresAt;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        magic.session.invalidateAccessToken();
                        return [4 /*yield*/, this.keycloak.updateToken(-1)];
                    case 1:
                        _b.sent();
                        magic.session.setSessionId(this.keycloak.sessionId);
                        token = this.keycloak.token;
                        refreshToken = this.keycloak.refreshToken;
                        idToken = this.keycloak.idToken;
                        expiresAt = (_a = this.keycloak.tokenParsed) === null || _a === void 0 ? void 0 : _a.exp;
                        magic.session.refreshKeycloakSession({
                            token: token,
                            refreshToken: refreshToken,
                            idToken: idToken,
                            expiresAt: expiresAt,
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    KeycloakService.prototype.logoutWithDelay = function (redirectUri) {
        var _this = this;
        notificationService.show(translate('logout.notification.text'), {
            style: NotificationStyle.INFO,
            uniqueID: 'logout.notification.text',
        });
        setTimeout(function () {
            _this.logout(redirectUri);
        }, 3000);
    };
    KeycloakService.prototype.getLocale = function () {
        return getLanguageTagBCP47(magicConfig.language);
    };
    KeycloakService.prototype.destroyed = function () {
        var _a, _b;
        (_a = this.userProfileUpdatedSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        (_b = this.userPromotionsSubscription) === null || _b === void 0 ? void 0 : _b.unsubscribe();
    };
    return KeycloakService;
}());

export { KeycloakService };
